import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import AsyncSelect, { components } from 'react-select';
import postal from 'postal';

import Loading from '../../../../shared/loading';
import ErrorBoundary from '../../../../shared/error-boundary';
import getAll from '../../../../services/mentionables/mentionables-service';

const GroupLabel = ({ label }) => <div>{label}</div>;
GroupLabel.propTypes = { label: PropTypes.string.isRequired };

const Option = ({
  data, isFocused, innerRef, innerProps,
}) => (
  <div
    className={isFocused ? 'select__option select__option--is-focused' : 'select__option'}
    ref={innerRef}
    {...innerProps}
  >
    <div className="option-type">{data.type}</div>
    <div className="option-title">{data.isCompanyProfile && data.company ? data.company : data.name}</div>
    {!data.isCompanyProfile && <div className="option-sub-title">{data.company}</div>}
  </div>
);

Option.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.string,
    name: PropTypes.string,
    company: PropTypes.string,
    isCompanyProfile: PropTypes.bool,
  }).isRequired,
  isFocused: PropTypes.bool.isRequired,
  innerRef: PropTypes.elementType,
  // eslint-disable-next-line react/forbid-prop-types
  innerProps: PropTypes.shape({}),
};

Option.defaultProps = {
  innerRef: null,
  innerProps: {},
};

const Menu = props => (
  <components.Menu {...props}>
    <span className="indicator" />
    {props.children}
  </components.Menu>
);
Menu.propTypes = { children: PropTypes.node.isRequired };

const onBlur = () => {
  postal.publish({
    channel: 'editor',
    topic: 'mention-control-on-blur',
  });
};

const onChange = mentionable => {
  postal.publish({
    channel: 'editor',
    topic: 'mention-control-value-selected',
    data: mentionable,
  });
};

const validSearchQuery = query => query.trim().length >= 2;

export default class MentionControl extends React.Component {
  static propTypes = {
    menuIsOpen: PropTypes.bool.isRequired,
  };

  constructor(props, context) {
    super(props, context);

    this.state = {
      query: '',
      mentionables: [],
    };

    this.setFocus = this.setFocus.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.noOptionsMessage = this.noOptionsMessage.bind(this);
  }

  onKeyDown(event) {
    const { key } = event;

    if (key === 'Enter' && !this.selectRef.select.state.focusedOption) {
      event.preventDefault();
    }

    if (key === 'Backspace' && this.selectRef.state.inputValue.length < 1) {
      postal.publish({
        channel: 'editor',
        topic: 'mention-control-on-backspace',
        data: event,
      });

      return;
    }

    postal.publish({
      channel: 'editor',
      topic: 'mention-control-on-keydown',
      data: event,
    });
  }

  onInputChange(newValue) {
    if (validSearchQuery(newValue)) {
      getAll(newValue)
        .then(result => this.setState({
          query: newValue,
          mentionables: _.map(_.get(result, 'data.mentionables'), m => ({
            ..._.pick(m, ['id', 'name', 'company', 'isCompanyProfile', 'type', 'publicUrl']),
            value: this.buildSearchName(m),
            label: m.name,
          })),
        }));
    } else {
      this.setState({
        query: newValue,
        mentionables: [],
      });
    }
  }

  setFocus() {
    this.selectRef.focus();
  }

  // eslint-disable-next-line class-methods-use-this
  buildSearchName(record) {
    let name = `${record.name} ${record.company}`;

    if (record.type === 'StockCode') {
      [':', ' ', ''].forEach(char => {
        name += ` ${record.name.replace(': ', char)}`;
      });
    }

    return name;
  }

  noOptionsMessage() {
    if (validSearchQuery(this.state.query)) {
      return 'NO CONTRIBUTOR, FUND OR STOCK CODE FOUND';
    }

    return 'ENTER A SEARCH TERM';
  }

  render() {
    const label = 'MENTION CONTRIBUTOR, FUND, OR STOCK CODE';
    const groupedOptions = [
      {
        label,
        options: this.state.mentionables,
      },
    ];

    return (
      <ErrorBoundary>
        <aside className="sidebar-item">
          <Loading variant="inline" ref={el => (this.loading = el)}>
            <section className="form-group">
              <AsyncSelect
                ref={el => (this.selectRef = el)}
                className="select-container"
                classNamePrefix="select"
                name="mentionables"
                value={null}
                components={{ Menu, Option }}
                onChange={onChange}
                onInputChange={_.debounce(this.onInputChange, 300)}
                onBlur={onBlur}
                onKeyDown={this.onKeyDown}
                options={groupedOptions}
                placeholder={groupedOptions[0].label}
                formatGroupLabel={GroupLabel}
                menuIsOpen={this.props.menuIsOpen}
                noOptionsMessage={this.noOptionsMessage}
              />
            </section>
          </Loading>
        </aside>
      </ErrorBoundary>
    );
  }
}
